import React from "react"
import type { SnapshotFrom, ActorRefFrom } from "xstate"
import { Progress } from "@carrotfertility/carotene-core"
import { bookingFlowMachine } from "../bookingFlowMachine"
import type { InsuranceMember } from "../insuranceTypes"
import { InsuranceCheckPage, type InsuranceBridgeConfig } from "./InsuranceCheckPage"
import { BookingFlowError } from "./BookingFlowError"
import { InsuranceLayout } from "./insurance/InsuranceLayout"
import { InsuranceDetailsStep } from "./insurance/InsuranceDetailsStep"
import { InsuranceCheckingStep } from "./insurance/InsuranceCheckingStep"
import { InsuranceCoveredStep } from "./insurance/InsuranceCoveredStep"
import { InsuranceUnavailableStep } from "./insurance/InsuranceUnavailableStep"
import { useBookingPaymentOptions } from "../hooks"

type Props = {
  state: SnapshotFrom<typeof bookingFlowMachine>
  send: ActorRefFrom<typeof bookingFlowMachine>["send"]
  bridgeConfig?: InsuranceBridgeConfig
  member?: InsuranceMember
  onContinue: () => void
  onExit?: () => void
}

export function InsuranceBookingStep(props: Props): JSX.Element {
  return (
    <InsuranceLayout>
      <InsuranceBookingContents {...props} />
    </InsuranceLayout>
  )
}

function InsuranceBookingContents({ state, send, bridgeConfig, member, onContinue, onExit }: Props): JSX.Element {
  const { program, appointmentType, insuranceInputs: inputs, insuranceResult: result } = state.context
  const options = useBookingPaymentOptions(program, appointmentType)
  React.useEffect(() => {
    send({ type: "PAYMENT_OPTIONS_LOADED", paymentOptions: options.data ?? null })
  }, [options.data, send])
  if (options.isLoading) return <Progress aria-label="Loading" />
  if (!options.data || options.data.insurance.status !== "available") {
    return <BookingFlowError onRetry={() => void options.refetch()} onExit={onExit} exitLabel="Back to Care Team" />
  }
  return (
    <InsuranceCheckPage
      bridgeConfig={bridgeConfig}
      member={member}
      serviceTypeIds={options.data.insurance.bridgeServiceTypeIds}
      onInsuranceEvent={send}
      onBack={() => send({ type: "INSURANCE_BACK" })}
      onExit={onExit}
    >
      {({ submit, memberReady }) => {
        switch (true) {
          case state.matches({ insurance: "details" }):
          case state.matches({ insurance: "error" }):
            return (
              <InsuranceDetailsStep
                inputs={inputs}
                verificationError={state.context.insuranceError}
                onSubmit={submit}
                memberReady={memberReady}
                onExit={onExit}
              />
            )
          case state.matches({ insurance: "checking" }):
            return <InsuranceCheckingStep />
          case state.matches({ insurance: "covered" }):
            return inputs && result ? (
              <InsuranceCoveredStep onContinue={onContinue} />
            ) : (
              <BookingFlowError onExit={onExit} />
            )
          case state.matches({ insurance: "notCovered" }):
            return (
              <InsuranceUnavailableStep reason="notCovered" paymentOptions={options.data} onContinue={onContinue} />
            )
          case state.matches({ insurance: "noProviders" }):
            return (
              <InsuranceUnavailableStep reason="noProviders" paymentOptions={options.data} onContinue={onContinue} />
            )
          default:
            return null
        }
      }}
    </InsuranceCheckPage>
  )
}
